import { inject, Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
} from '@angular/common/http';
import {
    EMPTY,
    Observable,
    catchError,
    finalize,
    from,
    lastValueFrom,
    throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '@auth0/auth0-angular';
import { LoadingService } from '../services/loading/loading.service';
import { AlertService } from '../services/alert/alert.service';
import { MessageConstants } from '../constants/message-constants';
import {
    SUPPRESS_GLOBAL_SPINNER,
    SUPPRESS_NETWORK_ERRORS,
} from '../constants/http-context-tokens';

@Injectable()
export class BaseUrlInterceptor implements HttpInterceptor {
    private readonly baseApiUrl = environment.apiUrl;
    private readonly auth0Service = inject(AuthService);
    private readonly loadingService = inject(LoadingService);
    private readonly alertService = inject(AlertService);

    constructor() {}

    /* eslint-disable  @typescript-eslint/no-explicit-any */
    intercept(
        request: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        const suppressGlobalSpinner = request.context.get(
            SUPPRESS_GLOBAL_SPINNER,
        );

        if (!suppressGlobalSpinner) {
            this.loadingService.show();
        }

        if (this.isCoordinationApiRequest(request.url)) {
            return from(
                this.handleCoordinationApiRequest(
                    request,
                    next,
                    suppressGlobalSpinner,
                ),
            );
        }

        return next.handle(request).pipe(
            finalize(() => {
                if (!suppressGlobalSpinner) {
                    this.loadingService.hide();
                }
            }),
        );
    }

    /**
     * Handles the coordination API request by adding the base URL and the bearer token.
     * If the request body is not FormData, sets the Content-Type header to application/json.
     * If it is, then leaves the Content-Type header unset so the browser can set it with the correct boundary.
     *
     * @param request the request to handle
     * @param next the next handler to call
     * @param suppressGlobalSpinner whether the global loading spinner was not shown for this request
     * @returns the response from the request
     */
    async handleCoordinationApiRequest(
        request: HttpRequest<any>,
        next: HttpHandler,
        suppressGlobalSpinner = false,
    ) {
        if (!(request.body instanceof FormData)) {
            request.headers.set('Content-Type', 'application/json');
        }

        const token = await this.getAuthBearerToken();
        const apiRequest = request.clone({
            url: `${this.baseApiUrl}/${request.url}`,
            headers: request.headers.set('Authorization', `Bearer ${token}`),
        });

        return lastValueFrom(
            next.handle(apiRequest).pipe(
                catchError((error) => {
                    if (
                        request.context.get(SUPPRESS_NETWORK_ERRORS) &&
                        this.isNetworkError(error)
                    ) {
                        return EMPTY;
                    }

                    this.handleErrorAlert(error);
                    return throwError(error);
                }),
                finalize(() => {
                    if (!suppressGlobalSpinner) {
                        this.loadingService.hide();
                    }
                }),
            ),
        );
    }

    private handleErrorAlert(response: HttpErrorResponse): void {
        this.alertService.displayErrorAlert(
            MessageConstants.apiErrorMesage,
            true,
            {
                apiErrorGuid: response.error?.ErrorId,
            },
        );
    }

    /**
     * Returns whether the URL is a coordination API request.
     * @param url the url to check
     * @returns true if the url is a coordination API request, false otherwise
     */
    private isCoordinationApiRequest(url: string): boolean {
        return url.startsWith('api/');
    }

    /**
     * Determines if an HTTP error is a network/timeout failure.
     * Checks for status 0 (no HTTP response) and ProgressEvent (browser network error).
     * Note: This detects all network failures, not just timeouts (offline, CORS, etc.).
     * @param error the HTTP error response to check
     * @returns true if the error appears to be a network/timeout error, false otherwise
     */
    private isNetworkError(error: HttpErrorResponse): boolean {
        return error.error instanceof ProgressEvent && error.status === 0;
    }

    /**
     * Gets the Auth0 Bearer token from the Auth0 service.
     * @returns either the token or an error if the token is not found
     */
    public async getAuthBearerToken(): Promise<any> {
        return new Promise<string>((resolve, reject) => {
            this.auth0Service.idTokenClaims$.subscribe((claims) => {
                if (claims && claims.__raw) {
                    resolve(claims.__raw);
                } else {
                    reject(new Error('Bearer token not found'));
                }
            });
        });
    }
    /* eslint-enable  @typescript-eslint/no-explicit-any */
}
